// eslint-disable-next-line import/no-extraneous-dependencies
import { SetURLSearchParams } from "react-router-dom"

import { COMPLETION_STATUS } from "../components/submitApplication/single/draftCompletionStatus/draftCompletionStatus"
import { LCT_TILES } from "../components/submitApplication/single/selectLct/applicationTypeTiles"
import {
    DRAFT_ID,
    DRAFT_UPDATED_DATE_FROM,
    DRAFT_UPDATED_DATE_TO,
    ORDER_BY_TIMESTAMP,
    POSTCODE,
    STATUS_FROM,
    STATUS_TO,
    TECHNOLOGY,
} from "../constants/generalConstants"

/**
 * Hook for deriving and managing filter panel input values from URL search
 * params for installer draft page. database search API.
 * Search params are treated as the filter panel state. This hook abstracts the
 * translation of this state into objects
 * the filter panel inputs can read.
 *
 * @param searchParams - URL search parameters
 * @param setSearchParams - setter function for URL search parameters
 * @returns - helper function for setting a filter value and values for all
 * database filter panel options.
 */
export const useDraftsApplicationFilters = (
    searchParams: URLSearchParams,
    setSearchParams: SetURLSearchParams
): {
    draftId: string
    clearAllFilters: () => void
    setFilterFromTextfield: (
        newValue: string | number | null,
        searchParam: string
    ) => void
    orderBy: string
    orderDirection: string
    dateFrom: string
    dateTo: string
    status: string | null
    statusFrom: number | null
    statusTo: number | null
    technology: string | null
    postcode: string | null
    setOrderBy: (orderBy: string, orderDirection: string) => void
} => {
    /**
     * Updates URL search params for filter panel elements which are text
     * @param newValue - new value being set
     * @param searchParam - url search param for the textfield
     */
    const setFilterFromTextfield = (
        newValue: string | number | null,
        searchParam: string
    ): void => {
        if (newValue) {
            searchParams.set(searchParam, String(newValue))
        } else {
            searchParams.delete(searchParam)
        }
        setSearchParams(searchParams)
    }

    /**
     * Sets order by and order direction URL params
     * @param orderBy - column to be sorted on
     * @param orderDirection - sort direction (asc | desc)
     */
    const setOrderBy = (orderBy: string, orderDirection: string): void => {
        searchParams.set(orderBy, orderDirection)
        setSearchParams(searchParams)
    }

    /**
     * Gets the title from COMPLETION_STATUS
     */
    const getStatusTitle = (statusFrom: number | null): string | null => {
        if (statusFrom === null) return null
        const status = COMPLETION_STATUS.find(
            (completionStatus) => completionStatus.statusFrom === statusFrom
        )
        return status ? status.title : null
    }

    /**
     * Gets the title from LCT_TILES
     */
    const getTechnologyTitle = (
        technologyKey: string | null
    ): string | null => {
        if (technologyKey === null) return null
        const technology = LCT_TILES.find(
            (tile) => tile.value === technologyKey
        )
        return technology ? technology.title : null
    }

    // Filter params
    const draftId = searchParams.get(DRAFT_ID) || ""
    const dateFrom = searchParams.get(DRAFT_UPDATED_DATE_FROM) || ""
    const dateTo = searchParams.get(DRAFT_UPDATED_DATE_TO) || ""
    const orderBy = ORDER_BY_TIMESTAMP
    const orderDirection = searchParams.get(ORDER_BY_TIMESTAMP) || "asc"
    const statusFrom = searchParams.get(STATUS_FROM)
        ? Number(searchParams.get(STATUS_FROM))
        : null
    const statusTo = searchParams.get(STATUS_TO)
        ? Number(searchParams.get(STATUS_TO))
        : null
    const status = getStatusTitle(statusFrom)
    const technologyKey = searchParams.get(TECHNOLOGY) || null
    const technology = getTechnologyTitle(technologyKey)
    const postcode = searchParams.get(POSTCODE) || null

    const clearAllFilters = (): void => {
        setSearchParams(new URLSearchParams())
    }
    return {
        draftId,
        orderBy,
        orderDirection,
        dateFrom,
        dateTo,
        status,
        statusTo,
        statusFrom,
        technology,
        postcode,
        setOrderBy,
        setFilterFromTextfield,
        clearAllFilters,
    }
}
